import moment, { Moment } from "moment";

import {
  PracticeCropWithDefaults,
  TFunction,
  computeCarbonProgramImpact,
  getCurrentReportingValues,
  getEnvironment,
  getIsSubmissionPeriodOpen,
  isAutoEnrolledInventoryProgram,
  isFieldEnrolled,
  isOutsideExpectedRange,
} from "utils";

import { FERTILIZER_BUNDLE_SLUGS, HarvestUnitValue, TILLAGE_SEEDING_BUNDLE_SLUGS, toTonnesPerHa } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  AccountingMethodology,
  AllCropsPracticeDataByYear,
  AllCropsPracticeDataItem,
  CROP_TYPE_FCPO,
  CarbonProgram,
  CarbonProgramOutcomesResponse,
  CropFCPO,
  CropItemData,
  CropPracticesRequestBody,
  CropsWithMissingHistoricYieldDataByCropId,
  CropsWithMissingHistoricalPracticeDataByYear,
  CropsWithMissingHistoricalPracticeDataItem,
  DataSubmissionDatesResponse,
  EDITING_REQUEST_TYPE,
  EditingRequestType,
  FieldListField,
  FieldOutcomesByPractice,
  FieldPracticeResponse,
  GetVerificationEditingRequestsResponse,
  ItemWithMissingData,
  ItemsWithMissingData,
  ItemsWithMissingDataByFieldId,
  PRACTICE_SLUG,
  PRACTICE_TYPE,
  PracticeBundle,
  PracticeFCPO,
  PracticeFCPORequest,
  PracticeSlug,
  TimelineCalendarItemsProps,
  VerificationEditingRequest,
  VerificationsFarmTasks,
  ZERO_UUID,
} from "models";

import { FIELD_SORT_OPTION } from "pages/app/all-field-view/components/FieldSortOptions";
import { PracticeConfigForBulk } from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import { getInputRequirementConfig, isRequired } from "pages/app/all-field-view/utils/getInputRequirementConfig";
import { getCropPracticeType } from "pages/app/field-timeline/utils/CropUtils";
import { calculateAllotmentPayouts, capPayoutAtEligible } from "pages/app/field-timeline/utils/PayoutUtils";
import { getFieldBaselineYearRange } from "pages/app/field-timeline/utils/PracticeUtils";
import { type MissingDataForm } from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";

// narrows a selection down to the crops a bulk save reported as skipped — used by AllFieldsDataTable's onCropsSkipped
export const narrowSelectionToSkippedCrops = (selectedItems: Map<string, MissingDataForm>, skippedCropIds: string[]): Map<string, MissingDataForm> => {
  const skippedCropIdSet = new Set(skippedCropIds);
  const stillSelectedItems = new Map<string, MissingDataForm>();
  selectedItems.forEach((item, cropId) => {
    if (skippedCropIdSet.has(cropId)) stillSelectedItems.set(cropId, item);
  });
  return stillSelectedItems;
};

const MANDATORY_FUTURE_SLUGS: PracticeSlug[] = [...TILLAGE_SEEDING_BUNDLE_SLUGS, ...FERTILIZER_BUNDLE_SLUGS];
const BASIC_PRACTICE_SLUGS: PracticeSlug[] = [
  PRACTICE_SLUG.CROPS,
  PRACTICE_SLUG.TILLAGES,
  PRACTICE_SLUG.SEEDINGS,
  PRACTICE_SLUG.MINERAL_FERTILIZERS,
  PRACTICE_SLUG.ORGANIC_FERTILIZERS,
];

const calculatePreviousCrop = (fieldCrops: CropFCPO[], crop: CropFCPO) =>
  fieldCrops
    // sort crops on field in reverse chronological order
    .toSorted(
      (a, b) => moment(b.harvestEndDate ?? b.fallbackDates.harvestEndDate).valueOf() - moment(a.harvestEndDate ?? a.fallbackDates.harvestEndDate).valueOf()
    )
    // we're only interested in main crops
    .filter((otherCrop) => otherCrop.cropType === CROP_TYPE_FCPO.MAIN)
    // we are essentially searching backwards, for the first crop that was harvested before the current crop was harvested
    // (some historical main crops overlap, so we can't compare current crop seeding date with the previous crop's harvest date)
    .find((otherCrop) =>
      moment(otherCrop.harvestEndDate ?? otherCrop.fallbackDates.harvestEndDate).isBefore(moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate))
    );

export const getExistingPracticesAppliedStatus = (
  practices: PracticeFCPO[]
): {
  defaultToggleState: boolean | null;
  existingPractices: PracticeFCPO[];
} => {
  const existingPracticesWithNoZeroId = practices.filter((practice) => practice.assignedPracticeId !== ZERO_UUID);
  const intentionallyNo = !!practices.find((practice) => practice.assignedPracticeId === ZERO_UUID);
  const intentionallyYes = existingPracticesWithNoZeroId.length > 0;

  // Note: This will be true if there is at least one practice with no ZERO_UUID
  const defaultToggleState = intentionallyYes ? true : intentionallyNo ? false : null;

  return { defaultToggleState, existingPractices: existingPracticesWithNoZeroId };
};

export const determinePracticeSettings = ({
  practiceConfigForBulk,
  practices,
  disabled,
}: {
  practices: PracticeFCPO[];
  disabled: boolean;
  practiceConfigForBulk?: PracticeConfigForBulk;
}): {
  defaultToggleState: boolean | null;
  toggleDisabled: boolean;
  existingPractices: PracticeFCPO[];
  hasMixedValues?: boolean;
} => {
  if (!practiceConfigForBulk) {
    const { existingPractices, defaultToggleState } = getExistingPracticesAppliedStatus(practices);
    return { existingPractices, defaultToggleState, toggleDisabled: disabled };
  }

  const { toggleState, toggleDisabled, bulkPracticeState, practiceToShow } = practiceConfigForBulk;
  return {
    defaultToggleState: toggleState,
    existingPractices: practiceToShow,
    toggleDisabled,
    hasMixedValues: bulkPracticeState === "MIXED" || bulkPracticeState === "ONLY_ONE_CROP_WITH_PRACTICES",
  };
};

export const createCropPracticeRequestBody = (
  crop: CropFCPO,
  formPractices: PracticeFCPO[],
  isTillageApplied: boolean | null,
  isMineralApplied: boolean | null,
  isMineralActivated: boolean | null,
  isOrganicApplied: boolean | null,
  hasFertilizerManagement: boolean | null,
  existingPractices: PracticeFCPO[],
  isSoilPlus3Active: boolean
): CropPracticesRequestBody => {
  const practices: Omit<PracticeFCPORequest, "verificationStatus">[] = [];
  const isMineralActivate = (isSoilPlus3Active && isMineralActivated) || !isSoilPlus3Active;

  const emptyPracticeDate =
    crop.cropType === CROP_TYPE_FCPO.MAIN
      ? crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate
      : crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate;

  // Create ZERO_ID practices to indicate that the user has intentionally decided to leave out a practice (NO)
  if (isTillageApplied === false) {
    const emptyPractice = {
      startDate: moment(emptyPracticeDate).toISOString(),
      endDate: moment(emptyPracticeDate).toISOString(),
      type: PRACTICE_SLUG.TILLAGES,
      assignedPracticeId: ZERO_UUID,
    };
    practices.push(emptyPractice);
  }
  if (isMineralActivate && isMineralApplied === false) {
    const emptyPractice = {
      startDate: moment(emptyPracticeDate).toISOString(),
      endDate: moment(emptyPracticeDate).toISOString(),
      type: PRACTICE_SLUG.MINERAL_FERTILIZERS,
      assignedPracticeId: ZERO_UUID,
      amount: 0,
    };
    practices.push(emptyPractice);
  }
  if (isOrganicApplied === false) {
    const emptyPractice = {
      startDate: moment(emptyPracticeDate).toISOString(),
      endDate: moment(emptyPracticeDate).toISOString(),
      type: PRACTICE_SLUG.ORGANIC_FERTILIZERS,
      assignedPracticeId: ZERO_UUID,
      amount: 0,
      applicationType: ZERO_UUID,
      nValue: 0,
      selfProduced: false,
    };
    practices.push(emptyPractice);
  }

  // Only add practices to the response body, if the user has intentionally decided to add practices (YES)
  if (isTillageApplied) {
    const tillages = formPractices.filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES);
    practices.push(...tillages);
  }
  if (isMineralActivate && isMineralApplied) {
    const mineralFertilizers = formPractices.filter((practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS);
    practices.push(...mineralFertilizers);
  }
  if (isOrganicApplied) {
    const organicFertilizers = formPractices.filter((practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS);
    practices.push(...organicFertilizers);
  }

  // Inject previous verification status
  const injectedPractices: PracticeFCPORequest[] = practices.map((practice) => {
    // Find the corresponding existng practice (based on id or if it has ZERO_UUID (e.g. no fertilizer applied))
    const existingPractice = existingPractices.find(
      (existingPractice) =>
        existingPractice.type === practice.type &&
        (("id" in practice && existingPractice.id === practice.id) ||
          (practice.assignedPracticeId === ZERO_UUID && existingPractice.assignedPracticeId === ZERO_UUID))
    );

    return {
      ...practice,
      verificationStatus: existingPractice?.verificationStatus ?? null,
    };
  });

  return {
    crop,
    practices: injectedPractices,
    hasFertilizerManagement,
  };
};

// get the year for a crop based on its seeding start and harvest end dates, this should be used just for data submission purposes, not accounting
export const getCropYearFromDates = (cropType: CROP_TYPE_FCPO, seedingStartDate: Moment, harvestEndDate: Moment): number | null => {
  const cropReferenceDate = cropType === CROP_TYPE_FCPO.MAIN ? harvestEndDate : seedingStartDate;
  if (!cropReferenceDate.isValid()) return null;

  return cropReferenceDate.year();
};

// the range to highlight for a single field's row on the timeline: from the previous reporting year's main crop
// harvest to the current reporting year's main crop harvest, falling back to the carbon program's reporting period
// when either main crop is missing
export const getFieldReportingPeriodHighlight = (
  fieldId: string,
  timelineItems: TimelineCalendarItemsProps<CropItemData>[],
  reportingYear: number,
  carbonProgram: CarbonProgram | null
): { startTime: number; endTime: number; isDefaultPeriod: boolean } => {
  const mainCropsForField = timelineItems.filter((item) => item.group === fieldId && !item.isPlaceholder && item.data?.cropType === CROP_TYPE_FCPO.MAIN);
  const currentMainCrop = mainCropsForField.find((item) => item.data?.year === reportingYear);
  const previousMainCrop = mainCropsForField.find((item) => item.data?.year === reportingYear - 1);
  const currentHarvest = currentMainCrop?.end_time;
  const previousHarvest = previousMainCrop?.end_time;

  const { getReportingYearRange } = getCurrentReportingValues(reportingYear, carbonProgram);
  const { start: fallbackStart, end: fallbackEnd } = getReportingYearRange(reportingYear);
  // No main crop in either boundary year — the whole band is the program's default reporting period, not a harvest-bounded range.
  const isDefaultPeriod = !currentHarvest && !previousHarvest;

  // No previous-year main crop, but the current one was seeded during the previous reporting period —
  // so span its own growing period instead of the reporting-period fallback.
  const { start: previousPeriodStart, end: previousPeriodEnd } = getReportingYearRange(reportingYear - 1);
  const currentCropSeededInPreviousPeriod =
    !previousHarvest && !!currentMainCrop && currentMainCrop.start_time.isBetween(previousPeriodStart, previousPeriodEnd, "day", "[]");

  return {
    startTime: (currentCropSeededInPreviousPeriod ? currentMainCrop!.start_time.clone().subtract(1, "day") : previousHarvest ?? fallbackStart).valueOf(),
    // +1 day so the highlight border doesn't sit directly on top of the harvested crop card's own edge — not applied
    // to the default period, whose end is the reporting period boundary rather than a rendered crop card's edge.
    endTime: (currentHarvest && !isDefaultPeriod ? currentHarvest.clone().add(1, "day") : fallbackEnd).valueOf(),
    isDefaultPeriod,
  };
};

export type BaselinePeriod = { startTime: number; endTime: number };

// calendar-based programs use whole calendar years, harvest-based programs go from harvest to harvest for each field
export const getFieldBaselinePeriod = (
  field: Pick<FieldListField, "id" | "baselineYears">,
  timelineItems: TimelineCalendarItemsProps<CropItemData>[],
  isHarvestMonitoringPeriod: boolean,
  carbonProgram: CarbonProgram | null
): BaselinePeriod | null => {
  const baselineYearRange = getFieldBaselineYearRange(field.baselineYears ?? []);
  if (!baselineYearRange) return null;

  if (!isHarvestMonitoringPeriod) {
    return {
      startTime: moment().year(baselineYearRange.startYear).startOf("year").valueOf(),
      endTime: moment().year(baselineYearRange.endYear).endOf("year").valueOf(),
    };
  }

  // the first baseline year's crop only marks the starting harvest, so the baseline runs from its harvest onwards
  const firstPeriodYear = Math.min(baselineYearRange.startYear + 1, baselineYearRange.endYear);
  const firstBaselinePeriod = getFieldReportingPeriodHighlight(field.id, timelineItems, firstPeriodYear, carbonProgram);
  const lastBaselinePeriod = getFieldReportingPeriodHighlight(field.id, timelineItems, baselineYearRange.endYear, carbonProgram);
  return { startTime: firstBaselinePeriod.startTime, endTime: lastBaselinePeriod.endTime };
};

// in harvest-based programs a cover crop or undersowing belongs to the period it is sown in.
// endTime is one day after the last baseline harvest, so a crop sown after that harvest is not baseline
export const isSownInBaselinePeriod = (seedingDate: Moment, baselinePeriod: BaselinePeriod): boolean =>
  seedingDate.valueOf() >= baselinePeriod.startTime && seedingDate.isBefore(baselinePeriod.endTime, "day");

export const isRelevantYear = (fieldOutcomes: FieldOutcomesByPractice, slug: PracticeSlug, year: number | undefined, reportingYear: number): boolean => {
  if (!fieldOutcomes[slug]?.years || !year) return false;
  if (year in (fieldOutcomes[slug]?.years || {})) return true;
  if (year === reportingYear + 1) {
    const isFutureMandatorySlug = MANDATORY_FUTURE_SLUGS.includes(slug);
    const isParticipating = fieldOutcomes[slug]?.isParticipating;

    if (!isFutureMandatorySlug || !isParticipating) return false;

    // Additional validation: only return true if there are actual field outcomes for this practice
    return !!fieldOutcomes[slug]?.required && Object.values(fieldOutcomes[slug]?.required || {}).some(Boolean);
  }

  return false;
};

// data saved within this many days before the date it describes is considered actual rather than planned
export const REVIEW_BUFFER_DAYS = 30;

export type CropDataToConfirm = { seedingData: boolean; harvestData: boolean };

// only current year main and cover crops can have data to confirm, older crop entries are considered confirmed by default
export const isCropInReviewScope = (crop: CropFCPO): boolean => {
  // placeholders and unsaved crops have no crop chosen, so there is nothing to review yet
  if (!crop.cropId) return false;
  if (![CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER].includes(crop.cropType)) return false;
  const seedingStartDate = crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate;
  const harvestEndDate = crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate;

  return getCropYearFromDates(crop.cropType, moment(seedingStartDate), moment(harvestEndDate)) === moment().year();
};

// data needs a review once its date has passed, unless it was last saved shortly before or after that date
export const isDataDueForReview = (date: string | null | undefined, updatedAt: string | null | undefined): boolean => {
  if (!date || !moment(date).isValid()) return false;
  if (moment().isBefore(moment(date), "day")) return false;
  if (!updatedAt || !moment(updatedAt).isValid()) return true;

  return moment(updatedAt).isBefore(moment(date).subtract(REVIEW_BUFFER_DAYS, "days"));
};

export const getCropSeedingReviewDate = (crop: CropFCPO): string | null => crop.seedingEndDate ?? crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate;

// crop name and seeding date are reviewed against the seeding date, harvest date, yield and residue against the harvest date
export const getCropDataToConfirm = (crop: CropFCPO, isSubmissionPeriodOpen: boolean): CropDataToConfirm => {
  if (!isSubmissionPeriodOpen || !isCropInReviewScope(crop)) return { seedingData: false, harvestData: false };

  return {
    seedingData: isDataDueForReview(getCropSeedingReviewDate(crop), crop.updatedAt),
    harvestData: isDataDueForReview(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate, crop.updatedAt),
  };
};

export const cropHasDataToConfirm = (crop: CropFCPO, isSubmissionPeriodOpen: boolean): boolean => {
  const { seedingData, harvestData } = getCropDataToConfirm(crop, isSubmissionPeriodOpen);
  return seedingData || harvestData;
};

// fertilizers and tillages are reviewed against their application date, using their own last update
export const isPracticeDueForReview = (
  practice: PracticeFCPO,
  crop: CropFCPO,
  practiceUpdatedAt: string | undefined,
  isSubmissionPeriodOpen: boolean
): boolean => {
  // without a known update time nothing is shown, so tags don't flash up while the timestamps load
  if (!practice.id || !practiceUpdatedAt) return false;
  if (!isSubmissionPeriodOpen || !isCropInReviewScope(crop)) return false;

  return isDataDueForReview(practice.endDate ?? practice.startDate, practiceUpdatedAt);
};

const filterEnrolledFields = (
  fields: FieldListField[],
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined,
  carbonProgram: CarbonProgram | null
): FieldListField[] =>
  fields.filter((field) => {
    const practiceBundle = allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [];
    const isEnrolled = isFieldEnrolled(practiceBundle, carbonProgram ?? null);
    return isEnrolled;
  });

const groupItemsByFieldId = (
  items: TimelineCalendarItemsProps<CropItemData>[]
): {
  [fieldId: string]: TimelineCalendarItemsProps<CropItemData>[];
} =>
  items.reduce(
    (acc, item) => {
      const fieldId = item.group;
      if (!acc[fieldId]) {
        acc[fieldId] = [item];
        return acc;
      }

      acc[fieldId].push(item);
      return acc;
    },
    {} as { [fieldId: string]: TimelineCalendarItemsProps<CropItemData>[] }
  );

export const getMandatoryYearsByPracticeSlug = (
  availablePracticeBundles: PracticeBundle[],
  carbonProgram: CarbonProgram,
  reportingYear: number
): { [practiceSlug: string]: number[] } => {
  // we require next year data if
  // 1. there are active practice participations other than crop
  // 2. All intervention programs
  const hasNonCropPracticeParticipations = availablePracticeBundles.some((participation) => participation.active && participation.slug !== PRACTICE_SLUG.CROPS);
  const lastMandatoryYear =
    reportingYear + (hasNonCropPracticeParticipations || carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory ? 1 : 0);

  return BASIC_PRACTICE_SLUGS.reduce(
    (acc, slug) => {
      const participation = availablePracticeBundles.find((participation) => participation.slug === slug);
      if (!participation || !participation.startYear) {
        acc[slug] = [];
        return acc;
      }

      // for inventory farms, there's no baseline concept, so the first required year is the reporting year
      // for intervention farms, we require a 3 year baseline

      // for Cargill, because the monitoring period depends on the crops, we ask for 4 years to be safe
      // even though there are 3 baseline years, like any other program

      const effectiveBaselineYears = carbonProgram.fieldsManagement.monitoringPeriodType === "harvest" ? 4 : 3;
      const firstBaselineYear =
        carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory ? reportingYear : participation.startYear - effectiveBaselineYears;
      // a discontinued participation was still mandatory for the years before it was discontinued, so cap the range there
      // instead of dropping it entirely (participation.active is false as soon as discontinuedYear is set, regardless of the year)
      const effectiveLastMandatoryYear = participation.discontinuedYear ? Math.min(lastMandatoryYear, participation.discontinuedYear - 1) : lastMandatoryYear;
      const filledYears =
        effectiveLastMandatoryYear >= firstBaselineYear
          ? Array.from({ length: effectiveLastMandatoryYear - firstBaselineYear + 1 }, (_, i) => firstBaselineYear + i)
          : [];
      acc[participation.slug] = filledYears;

      return acc;
    },
    {} as { [practiceSlug: string]: number[] }
  );
};

export type MandatoryYearsByPracticeSlug = { [practiceSlug: string]: number[] };

export const getFieldMandatoryYears = (
  availablePracticeBundles: PracticeBundle[],
  carbonProgram: CarbonProgram | null | undefined,
  activeFarmReportingYear: number | undefined
): MandatoryYearsByPracticeSlug =>
  carbonProgram && activeFarmReportingYear !== undefined
    ? getMandatoryYearsByPracticeSlug(availablePracticeBundles, carbonProgram, activeFarmReportingYear)
    : {};

// which missing data counts for a crop in the given year; shared by Timeline and the Table view
// TODO PE-1195: on top of the mandatory years, also check whether the practice is required in the carbon program
export const getCropMissingDataRules = (mandatoryYearsByPracticeSlug: MandatoryYearsByPracticeSlug, cropYear: number | undefined) => {
  const isMandatory = (practiceSlug: PracticeSlug) => cropYear !== undefined && (mandatoryYearsByPracticeSlug[practiceSlug] ?? []).includes(cropYear);
  const allMandatoryYears = Object.values(mandatoryYearsByPracticeSlug).flat();

  return {
    requiredPractices: {
      seedings: isMandatory(PRACTICE_SLUG.SEEDINGS),
      tillages: isMandatory(PRACTICE_SLUG.TILLAGES),
      organicFertilizers: isMandatory(PRACTICE_SLUG.ORGANIC_FERTILIZERS),
    },
    isMineralFertilizerRequired: isMandatory(PRACTICE_SLUG.MINERAL_FERTILIZERS),
    // a crop older than the field's baseline doesn't affect the payout, so Timeline shows no missing data for it
    isBeforeMandatoryYears: cropYear !== undefined && allMandatoryYears.length > 0 && cropYear < Math.min(...allMandatoryYears),
  };
};

export type CropWithMissingDataTags = { crop: CropFCPO; tags: string[] };

// a yield outside the crop's expected range only counts as complete once the farmer has explained it
export const isCropYieldMissing = (
  crop: CropFCPO,
  practiceCrops: PracticeCropWithDefaults[] | undefined,
  cropParameters: PracticeManagement.CropParameter[] | undefined
): boolean => {
  if (typeof crop.yieldPerHa !== "number") return true;

  const cropDetails = practiceCrops?.find((practiceCrop) => practiceCrop.id === crop.cropId);
  const isZeroYieldAllowed = cropDetails?.harvestIsMandatory === undefined ? false : !cropDetails.harvestIsMandatory;
  if (!isZeroYieldAllowed && crop.yieldPerHa === 0) return true;

  const harvestTperHa = cropParameters?.find((cropParameter) => cropParameter.cropId === crop.cropId)?.harvestTonsPerHectare;
  return isOutsideExpectedRange(toTonnesPerHa(crop.yieldPerHa, crop.yieldPerHaUnit), harvestTperHa) && !crop.sanityNoteYield;
};

// per-crop missing-practice tags, checked against the field's mandatory years per practice slug
export const getFieldCropsWithMissingTags = (
  crops: CropFCPO[],
  practices: PracticeFCPO[],
  mandatoryYearsByPracticeSlug: { [practiceSlug: string]: number[] },
  carbonProgram: CarbonProgram | null,
  availablePracticeBundles: PracticeBundle[],
  isEnrolled: boolean,
  practiceCrops: PracticeCropWithDefaults[] | undefined,
  cropParameters?: PracticeManagement.CropParameter[]
): CropWithMissingDataTags[] => {
  const isYearMandatoryFor = (slug: PracticeSlug, year: number | undefined) => !!year && (mandatoryYearsByPracticeSlug[slug] ?? []).includes(year);

  return crops
    .filter((crop) => crop.cropType === CROP_TYPE_FCPO.MAIN || crop.cropType === CROP_TYPE_FCPO.COVER)
    .map((crop) => {
      const tags: string[] = [];

      if (isYearMandatoryFor(PRACTICE_SLUG.SEEDINGS, crop.year) && !crop.seedingPracticeType) tags.push("seedingType");

      if (crop.cropType === CROP_TYPE_FCPO.MAIN && crop.wasHarvested !== false && isYearMandatoryFor(PRACTICE_SLUG.CROPS, crop.year)) {
        const inputRequirementConfig = getInputRequirementConfig(carbonProgram, availablePracticeBundles, crop, isEnrolled);
        if (isRequired(inputRequirementConfig?.yield) && isCropYieldMissing(crop, practiceCrops, cropParameters)) tags.push("yield");
      }

      if (isYearMandatoryFor(PRACTICE_SLUG.TILLAGES, crop.year) && !practices.some((p) => p.type === PRACTICE_SLUG.TILLAGES && p.cropId === crop.id))
        tags.push("tillage");
      if (
        isYearMandatoryFor(PRACTICE_SLUG.ORGANIC_FERTILIZERS, crop.year) &&
        !practices.some((p) => p.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && p.cropId === crop.id)
      )
        tags.push("organicFertiliser");
      if (
        isYearMandatoryFor(PRACTICE_SLUG.MINERAL_FERTILIZERS, crop.year) &&
        !practices.some((p) => p.type === PRACTICE_SLUG.MINERAL_FERTILIZERS && p.cropId === crop.id)
      )
        tags.push("mineralFertiliser");

      return { crop, tags };
    })
    .filter(({ tags }) => tags.length > 0);
};

// mandatory years with no Main crop covering them at all
export const getFieldYearsWithoutMainCrop = (crops: CropFCPO[], mandatoryYearsByPracticeSlug: { [practiceSlug: string]: number[] }): number[] => {
  const mandatoryYears = Array.from(new Set(Object.values(mandatoryYearsByPracticeSlug).flat()));
  return mandatoryYears.filter(
    (year) =>
      !crops.some(
        (crop) =>
          crop.cropType === CROP_TYPE_FCPO.MAIN &&
          crop.cropId &&
          getCropYearFromDates(
            crop.cropType,
            moment(crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate),
            moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate)
          ) === year
      )
  );
};

// the years that need attention, combining crops with missing practice tags and years with no Main crop at all
export const getFieldMissingDataYears = (cropsWithMissingTags: CropWithMissingDataTags[], yearsWithoutMainCrop: number[]): number[] => {
  const missingDataYears = Array.from(new Set(cropsWithMissingTags.map(({ crop }) => crop.year).filter((year): year is number => year !== undefined)));
  return Array.from(new Set([...missingDataYears, ...yearsWithoutMainCrop])).sort((a, b) => a - b);
};

// shared by useFieldStatusMap (Map pin) and Field Details (payout badge), so both agree on "complete"
export const getFieldCompletenessStatus = (params: {
  crops: CropFCPO[];
  practices: PracticeFCPO[];
  availablePracticeBundles: PracticeBundle[];
  carbonProgram: CarbonProgram | null;
  activeFarmReportingYear: number | undefined;
  practiceCrops: PracticeCropWithDefaults[] | undefined;
  cropParameters?: PracticeManagement.CropParameter[];
}): { isComplete: boolean; allMissingDataYears: number[] } => {
  const { crops, practices, availablePracticeBundles, carbonProgram, activeFarmReportingYear, practiceCrops, cropParameters } = params;
  const isEnrolled = isFieldEnrolled(availablePracticeBundles, carbonProgram);
  // matches Field Details' own mandatoryYearsByPracticeSlug calc, which isn't gated on enrollment either
  const mandatoryYearsByPracticeSlug = getFieldMandatoryYears(availablePracticeBundles, carbonProgram, activeFarmReportingYear);

  const cropsWithMissingTags = getFieldCropsWithMissingTags(
    crops,
    practices,
    mandatoryYearsByPracticeSlug,
    carbonProgram,
    availablePracticeBundles,
    isEnrolled,
    practiceCrops,
    cropParameters
  );
  const yearsWithoutMainCrop = getFieldYearsWithoutMainCrop(crops, mandatoryYearsByPracticeSlug);
  const allMissingDataYears = getFieldMissingDataYears(cropsWithMissingTags, yearsWithoutMainCrop);

  return { isComplete: allMissingDataYears.length === 0, allMissingDataYears };
};

const getMissingCropsForField = (
  fieldItems: TimelineCalendarItemsProps<CropItemData>[] | undefined,
  mandatoryYearsByPracticeSlug: { [practiceSlug: string]: number[] }
): ItemWithMissingData[] => {
  if (!mandatoryYearsByPracticeSlug || Object.values(mandatoryYearsByPracticeSlug).every((years) => years.length === 0) || !fieldItems) return [];

  // we need to check for mandatory years for crops but also mandatory years for any other practices since they can only be assigned to a crop
  const mandatoryYears = Array.from(new Set(Object.values(mandatoryYearsByPracticeSlug).flat()));
  const yearsWithMissingCrops = Array.from(mandatoryYears).filter(
    (year) =>
      // when it comes to data submission, we don't care for the reporting period, we just need to check that the farm has a main crop with the harvest date in the calendar year
      !fieldItems?.some(
        (item) =>
          item.data?.cropType === CROP_TYPE_FCPO.MAIN &&
          item.data?.cropId &&
          !item.isPlaceholder &&
          getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time) === year
      )
  );

  return fieldItems
    .filter((item) => {
      const cropYear = getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time);
      if (!cropYear) return false;
      return item.data?.cropType === CROP_TYPE_FCPO.MAIN && yearsWithMissingCrops.includes(cropYear);
    })
    .map((item) => ({
      id: item.id.toString(),
      startTime: item.start_time,
    })) as ItemWithMissingData[];
};

const getCropsWithMissingPractices = (
  mainAndCoverCrops: TimelineCalendarItemsProps<CropItemData>[],
  mandatoryYearsByPracticeSlug: { [practiceSlug: string]: number[] }
): ItemWithMissingData[] =>
  mainAndCoverCrops.reduce((acc, item) => {
    const assignedPractices = item.data?.cropPractices?.practices || [];
    const itemYear = getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time);
    const itemId = item.id;
    if (!itemId || !itemYear) return acc;

    const { requiredPractices, isMineralFertilizerRequired } = getCropMissingDataRules(mandatoryYearsByPracticeSlug, itemYear);
    const { tillages: isTillageRequired, seedings: isSeedingRequired, organicFertilizers: isOrganicFertilizerRequired } = requiredPractices;

    const isMissingTillage = isTillageRequired && !assignedPractices.some((practice) => practice.type === PRACTICE_SLUG.TILLAGES);
    const isMissingSeeding = isSeedingRequired && !item.data?.cropPractices?.crops?.find((crop) => crop.id === item.id)?.seedingPracticeType;
    const isMissingMineralFertilizer =
      isMineralFertilizerRequired && !assignedPractices.some((practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS);
    const isMissingOrganicFertilizer =
      isOrganicFertilizerRequired && !assignedPractices.some((practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS);

    const hasMissingData = [isMissingTillage, isMissingSeeding, isMissingMineralFertilizer, isMissingOrganicFertilizer].some(Boolean);

    if (hasMissingData) {
      acc.push({
        id: item.id.toString(),
        startTime: item.start_time,
      });
    }

    return acc;
  }, [] as ItemWithMissingData[]);

const getPracticesWithMissingData = (
  mainAndCoverCrops: TimelineCalendarItemsProps<CropItemData>[],
  carbonProgram: CarbonProgram,
  availablePracticeBundles: PracticeBundle[],
  isFieldEnrolled: boolean,
  mandatoryYearsByPracticeSlug: { [practiceSlug: string]: number[] },
  crops: PracticeCropWithDefaults[] | undefined,
  cropParameters: PracticeManagement.CropParameter[] | undefined
): ItemWithMissingData[] =>
  mainAndCoverCrops
    .filter((item) => {
      const itemYear = getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time);
      if (!item.id || !itemYear) return false;

      const cropFCPO = item.data.cropPractices?.crops?.find((crop) => crop.id === item.id);
      if (!cropFCPO) return false;

      const inputRequirementConfig = getInputRequirementConfig(carbonProgram, availablePracticeBundles, cropFCPO, isFieldEnrolled);

      // check for missing yield for crops in mandatory years
      if (isRequired(inputRequirementConfig?.yield)) {
        const cropIsInMandatoryYear = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.CROPS].includes(itemYear);

        if (cropIsInMandatoryYear && cropFCPO.wasHarvested !== false && isCropYieldMissing(cropFCPO, crops, cropParameters)) return true;
      }

      // check for missing fertilizer management for organic fertilizer entries in mandatory years
      if (isRequired(inputRequirementConfig?.fertilizerManagement)) {
        const cropIsInMandatoryYear = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.ORGANIC_FERTILIZERS].includes(itemYear);

        if (cropIsInMandatoryYear) {
          const hasFertilizerManagement = item.data.fertilizerManagements.some(
            (fertilizerManagement) => fertilizerManagement.fieldCropId === item.id && typeof fertilizerManagement.implements4R === "boolean"
          );
          if (!hasFertilizerManagement) return true;
        }
      }

      // check for missing tillage depth for tillage entries in mandatory years
      if (isRequired(inputRequirementConfig?.tillageDepth)) {
        const cropIsInMandatoryYear = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.TILLAGES].includes(itemYear);

        if (cropIsInMandatoryYear) {
          const relevantTillagePractices =
            item.data?.cropPractices?.practices?.filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES && practice.cropId === item.id) || [];
          const hasTillageDepth = relevantTillagePractices.every(
            (practice) => practice.assignedPracticeId === ZERO_UUID || typeof practice.tillageDepth === "number"
          );
          if (!hasTillageDepth) return true;
        }
      }

      // check for missing application dates for required practices in mandatory years:
      // - excluding crops, which have a dedicated function to include undersowing crops
      // - excluding seedings, because we derive the application dates from the associated crop
      const requiredPracticeSlugs = Object.entries(mandatoryYearsByPracticeSlug)
        .filter(([slug, years]) => slug !== PRACTICE_SLUG.CROPS && slug !== PRACTICE_SLUG.SEEDINGS && years.length > 0)
        .map(([practiceSlug]) => practiceSlug);

      const relevantPractices =
        item.data?.cropPractices?.practices?.filter((practice) => {
          const isInMandatoryYear = mandatoryYearsByPracticeSlug[practice.type].includes(itemYear);
          return requiredPracticeSlugs.includes(practice.type) && isInMandatoryYear;
        }) ?? [];

      const hasMissingApplicationDates = relevantPractices.some((practice) => practice.assignedPracticeId !== ZERO_UUID && practice.endDate === null);

      if (hasMissingApplicationDates) return true;

      return false;
    })
    .map((item) => ({
      id: item.id,
      startTime: item.start_time,
    }))
    .filter((item) => item.id !== undefined) as ItemWithMissingData[];

const getCropsWithDataToConfirm = (mainAndCoverCrops: TimelineCalendarItemsProps<CropItemData>[], isSubmissionPeriodOpen: boolean): ItemWithMissingData[] =>
  mainAndCoverCrops
    .filter((item) => {
      const itemYear = getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time);
      if (!item.id || !itemYear) return false;
      const cropFCPO = item.data.cropPractices?.crops?.find((crop) => crop.id === item.id);
      if (!cropFCPO) return false;

      return cropHasDataToConfirm(cropFCPO, isSubmissionPeriodOpen);
    })
    .map((item) => ({
      id: item.id,
      startTime: item.start_time,
    }))
    .filter((item) => item.id !== undefined) as ItemWithMissingData[];

// Whether there's an open (uncompleted) editing request directly targeting this crop.
export const hasOpenCropEditingRequest = (verificationEditingRequests: VerificationEditingRequest[], cropId: string | number): boolean =>
  verificationEditingRequests.some(
    (request) => request.completedAt === null && request.requestType === EDITING_REQUEST_TYPE.CROP && request.fieldCropId === cropId
  );

// Whether there's an open editing request on one of this crop's own tillage/fertilizer/seeding practices —
// those are fixed via the crop's drawer too, even though the request doesn't target the crop itself.
export const hasOpenPracticeEditingRequest = (
  verificationEditingRequests: VerificationEditingRequest[],
  { seedingPracticeId, practiceIds }: { seedingPracticeId: string | undefined; practiceIds: string[] }
): boolean =>
  verificationEditingRequests.some((request) => {
    if (request.completedAt !== null) return false;

    if (request.requestType === EDITING_REQUEST_TYPE.SEEDING) {
      return !!seedingPracticeId && request.fieldSeedingId === seedingPracticeId;
    }

    if (
      request.requestType === EDITING_REQUEST_TYPE.TILLAGE ||
      request.requestType === EDITING_REQUEST_TYPE.MINERAL_FERTILIZER ||
      request.requestType === EDITING_REQUEST_TYPE.ORGANIC_FERTILIZER
    ) {
      return practiceIds.some((id) => id === request.fieldTillageId || id === request.fieldMineralFertilizerId || id === request.fieldOrganicFertilizerId);
    }

    return false;
  });

const getCropsWithVerificationEditingRequests = (
  fieldItems: TimelineCalendarItemsProps<CropItemData>[],
  verificationEditingRequests: GetVerificationEditingRequestsResponse | undefined
): ItemWithMissingData[] => {
  if (!fieldItems || fieldItems.length === 0 || !verificationEditingRequests || verificationEditingRequests.length === 0) return [];

  // A crop item is flagged either by a direct crop editing request, or by an editing request on one of
  // its own tillage/fertilizer/seeding practices — those are fixed via the crop's drawer too.
  const itemsWithOpenVerificationEditingRequests = fieldItems.reduce((acc, item) => {
    if (item.isPlaceholder) return acc;

    const practiceIds = (item.data?.cropPractices?.practices ?? []).map((practice) => practice.id).filter((id): id is string => !!id);

    const hasOpenEditingRequest =
      hasOpenCropEditingRequest(verificationEditingRequests, item.id) ||
      hasOpenPracticeEditingRequest(verificationEditingRequests, { seedingPracticeId: item.data?.seedingPracticeId, practiceIds });

    if (hasOpenEditingRequest) {
      acc.push({ id: item.id.toString(), startTime: item.start_time });
    }

    return acc;
  }, [] as ItemWithMissingData[]);

  const placeholdersWithOpenVerificationEditingRequests = fieldItems.reduce((acc, item) => {
    if (item.isPlaceholder && item.data?.isMandatory && item.data?.isFlaggedByVerifier) {
      acc.push({
        id: item.id.toString(),
        startTime: item.start_time,
      });
    }
    return acc;
  }, [] as ItemWithMissingData[]);

  return [...itemsWithOpenVerificationEditingRequests, ...placeholdersWithOpenVerificationEditingRequests].filter(
    (item) => item !== undefined
  ) as ItemWithMissingData[];
};

const getCropsWithMissingGrowingDates = (crops: TimelineCalendarItemsProps<CropItemData>[]): ItemWithMissingData[] =>
  crops
    .filter((crop) => {
      if (crop.isPlaceholder) return false;

      const isMissingGrowingPeriodDates = crop.data?.hasMissingGrowingDates;
      return isMissingGrowingPeriodDates;
    })
    .map((crop) => ({
      id: crop.id.toString(),
      startTime: crop.start_time,
    })) as ItemWithMissingData[];

export const isCoverCropMissingRedzoneException = (
  cropType: CROP_TYPE_FCPO,
  isMandatory: boolean | null | undefined,
  redzoneExceptionId: string | null | undefined
): boolean => {
  if (cropType === CROP_TYPE_FCPO.COVER) {
    if (isMandatory === null || isMandatory === undefined) return true;
    if (isMandatory === true) return false;
    if (isMandatory === false && redzoneExceptionId === null) return true;
  }
  return false;
};

const getCoverCropsWithMissingRedzoneException = (
  mainAndCoverCrops: TimelineCalendarItemsProps<CropItemData>[],
  isInRedzone: boolean,
  accountingMethodology?: AccountingMethodology
): ItemWithMissingData[] => {
  if (!isInRedzone || accountingMethodology === ACCOUNTING_METHODOLOGY.inventory) return [];

  const missingData: ItemWithMissingData[] = [];

  mainAndCoverCrops.forEach((item) => {
    const { cropType, redzoneExceptionId, isMandatoryCoverCrop } = item.data;
    if (isCoverCropMissingRedzoneException(cropType, isMandatoryCoverCrop, redzoneExceptionId)) {
      missingData.push({
        id: item.id.toString(),
        startTime: item.start_time,
      });
    }
  });

  return missingData;
};

type ItemsWithMissingDataByFieldIdProps = {
  fields: FieldListField[];
  carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse | undefined;
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined;
  timelineItems: TimelineCalendarItemsProps<CropItemData>[];
  carbonProgram: CarbonProgram | null;
  verificationEditingRequests: GetVerificationEditingRequestsResponse | undefined;
  dataSubmissionDates: DataSubmissionDatesResponse | undefined;
  reportingYear: number;
  crops: PracticeCropWithDefaults[] | undefined;
  cropParameters?: PracticeManagement.CropParameter[];
  submissionFeatureFlag?: boolean;
};
export const getItemsWithMissingDataByFieldId = ({
  fields,
  carbonProgramOutcomesResponse,
  allFieldsPractices,
  timelineItems,
  carbonProgram,
  verificationEditingRequests,
  dataSubmissionDates,
  reportingYear,
  submissionFeatureFlag,
  crops,
  cropParameters,
}: ItemsWithMissingDataByFieldIdProps): ItemsWithMissingDataByFieldId => {
  if (!carbonProgramOutcomesResponse || !carbonProgram) return {};

  const isInventoryProgram = carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;

  // in the inventory program, all fields are automatically enrolled
  const enrolledFields = isInventoryProgram ? fields : filterEnrolledFields(fields, allFieldsPractices, carbonProgram);
  if (enrolledFields.length === 0) return {};

  // in the inventory program, only crops in the current and future reporting period are relevant
  const isCropInRelevantYear = (item: TimelineCalendarItemsProps<CropItemData>) => {
    if (!isInventoryProgram) return true;
    const cropYear = getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time);
    return cropYear === null || cropYear >= reportingYear;
  };

  // filter out recommendation items, they are not real practices and shouldn't affect missing data calculations
  const itemsWithoutRecommendations = timelineItems.filter((item) => item.practiceType !== PRACTICE_TYPE.SIDE_CROP_RECOMMENDATION);
  const itemsByFieldId = groupItemsByFieldId(itemsWithoutRecommendations);

  return enrolledFields.reduce((acc, field) => {
    const isSubmissionPeriodOpen = getIsSubmissionPeriodOpen(dataSubmissionDates?.submissionStartDate, dataSubmissionDates?.submissionEndDate);

    const availablePracticeBundles = allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [];
    const mandatoryYearsByPracticeSlug = getMandatoryYearsByPracticeSlug(availablePracticeBundles, carbonProgram, reportingYear);
    const missingCrops = getMissingCropsForField(itemsByFieldId[field.id], mandatoryYearsByPracticeSlug);

    if (!itemsByFieldId[field.id] || itemsByFieldId[field.id]?.length === 0) {
      acc[field.id] = {
        cropsWithMissingGrowingDates: [],
        cropsWithMissingPractices: [],
        practicesWithMissingData: [],
        missingCrops,
        cropsWithDataToConfirm: [],
        cropsWithVerificationEditingRequests: [],
        coverCropsWithMissingRedzoneException: [],
        cropsWithMissingSecondaryDataStatus: [],
      };
      return acc;
    }

    const mainAndCoverCrops = itemsByFieldId[field.id].filter(
      (item) => !item.isPlaceholder && (item.data?.cropType === CROP_TYPE_FCPO.MAIN || item.data?.cropType === CROP_TYPE_FCPO.COVER)
    );

    const mandatoryCropYears = new Set(Object.values(mandatoryYearsByPracticeSlug).flat());
    const isCropInMandatoryYear = (item: TimelineCalendarItemsProps<CropItemData>) =>
      mandatoryCropYears.has(getCropYearFromDates(item.data?.cropType, item.start_time, item.end_time) ?? item.data?.year ?? 0);
    const cropsWithMissingGrowingDates = getCropsWithMissingGrowingDates(itemsByFieldId[field.id].filter(isCropInMandatoryYear));

    const cropsWithMissingSecondaryDataStatus = itemsByFieldId[field.id]
      .filter((item) => isSecondaryDataQuestionShown([item.data], carbonProgram.accountingMethodology) && item.data.isSecondaryData === null)
      .map((crop) => ({
        id: crop.id.toString(),
        startTime: crop.start_time,
      }));

    const cropsWithMissingPractices = getCropsWithMissingPractices(mainAndCoverCrops, mandatoryYearsByPracticeSlug);
    // we are mapping through enrolled fields, so we can safely pass "true" as value for "isFieldEnrolled"
    const practicesWithMissingData = getPracticesWithMissingData(
      mainAndCoverCrops,
      carbonProgram,
      availablePracticeBundles,
      true,
      mandatoryYearsByPracticeSlug,
      crops,
      cropParameters
    );
    const cropsWithDataToConfirm = submissionFeatureFlag
      ? getCropsWithDataToConfirm(mainAndCoverCrops.filter(isCropInRelevantYear), isSubmissionPeriodOpen)
      : [];
    const cropsWithVerificationEditingRequests = submissionFeatureFlag
      ? getCropsWithVerificationEditingRequests(itemsByFieldId[field.id], verificationEditingRequests)
      : [];
    const coverCropsWithMissingRedzoneException = getCoverCropsWithMissingRedzoneException(
      mainAndCoverCrops,
      field.isInRedzone,
      carbonProgram.accountingMethodology
    );

    acc[field.id] = {
      cropsWithMissingGrowingDates,
      cropsWithMissingPractices,
      practicesWithMissingData,
      missingCrops,
      cropsWithDataToConfirm,
      cropsWithVerificationEditingRequests,
      coverCropsWithMissingRedzoneException,
      cropsWithMissingSecondaryDataStatus,
    };

    return acc;
  }, {} as ItemsWithMissingDataByFieldId);
};

const getRelevantYearsForHistoricDataFill = (firstParticipationYear: number, accountingMethodology: AccountingMethodology): number[] => {
  // intentionally hardcoded 2025: the data fill is a one time thing and doesn't need to be dynamic
  const lastMandatoryYear = 2025;
  const firstYear = accountingMethodology === ACCOUNTING_METHODOLOGY.inventory ? firstParticipationYear : firstParticipationYear - 3;
  const numberOfYearsToFill = lastMandatoryYear - firstYear + 1;
  const relevantYears = Array.from({ length: numberOfYearsToFill }, (_, i) => firstYear + i);
  return relevantYears;
};

// This function is meant to be used for the yield historical data fill feature, therefore, it's not going to return data if the farm is assigned to an inventory program.
// The yield task is shown in the task list when getMissingHistoricYieldDataOpenTasksCount(...) > 0, i.e. when there is at least one crop-year with isCompleted === false.
// isCompleted is true when every entry for that crop-year has a non-null yield; for harvestable crops, yield === 0 is treated as "still missing" so the task stays open.
export const getCropsWithMissingHistoricYieldDataByCropId = (
  fields: FieldListField[],
  carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse | undefined,
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined,
  carbonProgram: CarbonProgram | null,
  reportingYear: number | undefined,
  crops?: PracticeCropWithDefaults[]
): CropsWithMissingHistoricYieldDataByCropId => {
  if (!carbonProgramOutcomesResponse || !carbonProgram) return {};
  if (carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory) return {};
  if (!allFieldsPractices) return {};

  const enrolledFields = filterEnrolledFields(fields, allFieldsPractices, carbonProgram);
  if (enrolledFields.length === 0) return {};

  const relevantCrops = enrolledFields
    .map((field) => {
      const fieldCrops = allFieldsPractices[field.id]?.crops;
      if (!fieldCrops || fieldCrops.length === 0) return [];

      const cropPracticeParticipationStartYear = allFieldsPractices[field.id]?.availablePracticeBundles?.find((bundle) => bundle.slug === PRACTICE_SLUG.CROPS)
        ?.startYear;
      if (!cropPracticeParticipationStartYear) return [];

      const relevantYears = getRelevantYearsForHistoricDataFill(cropPracticeParticipationStartYear, carbonProgram.accountingMethodology);
      if (relevantYears.length === 0) return [];

      const mainCropsInRelevantYears = fieldCrops.filter((crop) => {
        if (crop.cropType !== CROP_TYPE_FCPO.MAIN) return false;
        const cropYear = getCropYearFromDates(
          crop.cropType,
          moment(crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate),
          moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate)
        );
        if (!cropYear) return false;
        return relevantYears.includes(cropYear);
      });

      return mainCropsInRelevantYears;
    })
    .flat();

  return groupCropsByYieldCompleteness(
    relevantCrops.filter((crop) => {
      if (!reportingYear) return false;
      const cropYear = getCropYearFromDates(
        crop.cropType,
        moment(crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate),
        moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate)
      );
      return !!cropYear && cropYear < reportingYear;
    }),
    crops
  );
};

// Unconditioned sibling of getCropsWithMissingHistoricYieldDataByCropId for the all-fields data table view: every
// field/year is considered, regardless of enrollment, accounting methodology or the one-time historical fill window.
export const getAllCropsYieldDataByCropId = (
  fields: FieldListField[],
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined,
  crops?: PracticeCropWithDefaults[]
): CropsWithMissingHistoricYieldDataByCropId => {
  if (!allFieldsPractices) return {};

  const relevantCrops = fields.map((field) => allFieldsPractices[field.id]?.crops?.filter((crop) => crop.cropType === CROP_TYPE_FCPO.MAIN) ?? []).flat();

  return groupCropsByYieldCompleteness(relevantCrops, crops);
};

// Entries a historic yield submission will overwrite: no yield recorded yet, a zero placeholder, or flagged for soilPlus3 data collection.
export const needsHistoricYieldUpdate = (crop: CropFCPO): boolean =>
  crop.yieldPerHa === null || crop.yieldPerHa === 0 || crop.yieldSoilPlus3DataCollection === true;

// Shared by getCropsWithMissingHistoricYieldDataByCropId and getAllCropsYieldDataByCropId: groups already-filtered
// crops by crop id/year and computes each year's yield-completeness. Callers differ only in which crops they pass in.
const groupCropsByYieldCompleteness = (relevantCrops: CropFCPO[], crops?: PracticeCropWithDefaults[]): CropsWithMissingHistoricYieldDataByCropId => {
  const grouped = relevantCrops.reduce(
    (acc, crop) => {
      const cropYear = getCropYearFromDates(
        crop.cropType,
        moment(crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate),
        moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate)
      );
      if (!cropYear || !crop.cropId) return acc;

      if (!acc[crop.cropId]) {
        acc[crop.cropId] = { translationSlug: crop.translationSlug, years: {} };
      }
      if (!acc[crop.cropId].years[cropYear]) {
        acc[crop.cropId].years[cropYear] = { entries: [] as CropFCPO[] };
      }
      acc[crop.cropId].years[cropYear].entries.push(crop);

      return acc;
    },
    {} as { [cropId: string]: { translationSlug: string; years: { [year: number]: { entries: CropFCPO[] } } } }
  );

  return Object.entries(grouped).reduce<CropsWithMissingHistoricYieldDataByCropId>((acc, [cropId, cropData]) => {
    acc[cropId] = {
      translationSlug: cropData.translationSlug,
      years: Object.entries(cropData.years).reduce(
        (yearAcc, [yearStr, { entries }]) => {
          const year = Number(yearStr);

          // For crops which must be harvested, 0 is invalid and should not count as "completed" so the yield task stays open
          const allHaveYield = entries.every((crop) => {
            const value = crop.yieldPerHa;
            if (value && value > 0) return true;

            const cropDetails = crops && crops.find((c) => c.id === crop.cropId);
            if (!cropDetails?.harvestIsMandatory && value === 0) return true;

            return false;
          });
          const isPartOfSoilPlus3DataCollection = entries.some((crop) => crop.yieldSoilPlus3DataCollection === true);

          // Hydrate from the entries a submission would actually overwrite, so the displayed value matches what gets written.
          // If none need updating (every entry already has a verified yield), fall back to showing that verified yield instead.
          const updatableEntries = entries.filter(needsHistoricYieldUpdate);
          const updatableYieldValues = updatableEntries.map((crop) => crop.yieldPerHa).filter((v): v is number => v !== null);
          const allYieldValues = entries.map((crop) => crop.yieldPerHa).filter((v): v is number => v !== null);
          const yieldValuesToHydrateFrom = updatableYieldValues.length > 0 ? updatableYieldValues : allYieldValues;
          const allValuesToHydrateFromSame = yieldValuesToHydrateFrom.length > 0 && yieldValuesToHydrateFrom.every((v) => v === yieldValuesToHydrateFrom[0]);
          const defaultValue = allValuesToHydrateFromSame ? yieldValuesToHydrateFrom[0] : null;
          const sanityNote = (updatableEntries.find((crop) => crop.sanityNoteYield) ?? entries.find((crop) => crop.sanityNoteYield))?.sanityNoteYield ?? null;

          yearAcc[year] = { entries, defaultValue, sanityNote, isCompleted: allHaveYield, isPartOfSoilPlus3DataCollection };
          return yearAcc;
        },
        {} as CropsWithMissingHistoricYieldDataByCropId[string]["years"]
      ),
    };
    return acc;
  }, {});
};

export const getCropsWithMissingHistoricalPracticeDataByYear = (
  fields: FieldListField[],
  carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse | undefined,
  carbonProgram: CarbonProgram | null,
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined,
  reportingYear: number | undefined
): CropsWithMissingHistoricalPracticeDataByYear => {
  if (!carbonProgramOutcomesResponse || !carbonProgram || !reportingYear) return {};

  // in the inventory program, all fields are automatically enrolled
  const enrolledFields =
    carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory ? fields : filterEnrolledFields(fields, allFieldsPractices, carbonProgram);
  if (enrolledFields.length === 0) return {};
  if (!allFieldsPractices || Object.keys(allFieldsPractices).length === 0) return {};

  const cropsWithPractices = enrolledFields.flatMap((field) => {
    const fieldPractices = allFieldsPractices[field.id];
    if (!fieldPractices || fieldPractices.crops.length === 0) return [];

    const availablePracticeBundles = allFieldsPractices?.[field.id]?.availablePracticeBundles ?? [];
    const mandatoryYearsByPracticeSlug = getMandatoryYearsByPracticeSlug(availablePracticeBundles, carbonProgram, reportingYear);
    const mandatoryCropYears = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.CROPS];

    return fieldPractices.crops.reduce(
      (acc, crop) => {
        // we only want to include main and cover crops
        if (![CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER].includes(crop.cropType)) return acc;

        const cropYear = getCropYearFromDates(
          crop.cropType,
          moment(crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate),
          moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate)
        );
        // intentionally hardcoded 2025: the data fill is a one time thing and doesn't need to be dynamic
        if (!cropYear || !mandatoryCropYears.includes(cropYear) || cropYear > 2025) return acc;

        const hasMissingGrowingPeriodDates = crop.seedingStartDate === null || crop.harvestEndDate === null;
        const hasGrowingPeriodDatesSoilPlus3DataCollection = !!crop.growingDatesSoilPlus3DataCollection;

        const inputRequirementConfig = getInputRequirementConfig(carbonProgram, availablePracticeBundles, crop, true);

        const mandatoryTillageYears = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.TILLAGES];
        const mandatoryOrganicFertilizerYears = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.ORGANIC_FERTILIZERS];
        const mandatorySeedingYears = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.SEEDINGS];

        const tillages =
          mandatoryTillageYears.length > 0
            ? fieldPractices.practices.filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES && practice.cropId === crop.id)
            : null;
        const organicFertilizers =
          mandatoryOrganicFertilizerYears.length > 0
            ? fieldPractices.practices.filter((practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && practice.cropId === crop.id)
            : null;
        // keep `null` when seeding is not required, but use an empty array when it is required and missing
        const seedings = mandatorySeedingYears.length > 0 ? (crop.seedingPracticeType ? [crop.seedingPracticeType] : []) : null;

        const hasMissingTillages = tillages !== null && tillages.length === 0;
        // some crops may already have tillage and complete data but be missing tillage depth data, we need to make sure that is accounted for
        const isTillageDepthRequired = tillages !== null && isRequired(inputRequirementConfig?.tillageDepth);
        const hasMissingTillageDepth =
          isTillageDepthRequired && tillages.some((practice) => practice.tillageDepth === null && practice.assignedPracticeId !== ZERO_UUID);

        const hasMissingTillageEndDate =
          tillages !== null && tillages.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);

        const hasMissingOrganicFertilizers = organicFertilizers !== null && organicFertilizers.length === 0;
        const hasMissingOrganicFertilizerEndDate =
          organicFertilizers !== null && organicFertilizers.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);
        const hasMissingSeedings = seedings !== null && seedings.length === 0;

        const hasSoilPlus3DataCollectionTillages = tillages !== null && tillages.some((practice) => practice.soilPlus3DataCollection === true);
        const hasSoilPlus3TillageDepthTillages = tillages !== null && tillages.some((practice) => practice.tillageDepthSoilPlus3DataCollection === true);
        const hasSoilPlus3ApplicationDatesTillages =
          tillages !== null && tillages.some((practice) => practice.applicationDatesSoilPlus3DataCollection === true);
        const hasSoilPlus3DataCollectionOrganicFertilizers =
          organicFertilizers !== null && organicFertilizers.some((practice) => practice.soilPlus3DataCollection === true);
        const hasSoilPlus3ApplicationDatesOrganicFertilizers =
          organicFertilizers !== null && organicFertilizers.some((practice) => practice.applicationDatesSoilPlus3DataCollection === true);
        const hasSoilPlus3DataCollectionSeedings = seedings !== null && crop.seedingSoilPlus3DataCollection === true;

        const hasAnyMissingData =
          hasMissingGrowingPeriodDates ||
          hasMissingTillages ||
          hasMissingTillageDepth ||
          hasMissingTillageEndDate ||
          hasMissingOrganicFertilizers ||
          hasMissingOrganicFertilizerEndDate ||
          hasMissingSeedings;

        const isEditable =
          hasGrowingPeriodDatesSoilPlus3DataCollection ||
          hasSoilPlus3DataCollectionTillages ||
          hasSoilPlus3TillageDepthTillages ||
          hasSoilPlus3ApplicationDatesTillages ||
          hasSoilPlus3DataCollectionOrganicFertilizers ||
          hasSoilPlus3ApplicationDatesOrganicFertilizers ||
          hasSoilPlus3DataCollectionSeedings;

        // only include crops where at least one practice type is missing (include practices with the soilPlus3DataCollection or seedingSoilPlus3DataCollection flags set to true)
        const hasMissingOrEditableData = hasAnyMissingData || isEditable;
        const isHistoricalYear = cropYear < reportingYear;

        if (isHistoricalYear && hasMissingOrEditableData) {
          acc.push({
            year: cropYear,
            fieldId: field.id,
            crop,
            previousMainCrop: calculatePreviousCrop(fieldPractices.crops, crop),
            hasMissingData: hasAnyMissingData,
            isTillageDepthRequired,
            assignedPractices: {
              [PRACTICE_SLUG.TILLAGES]: tillages,
              [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: organicFertilizers,
              [PRACTICE_SLUG.SEEDINGS]: seedings,
            },
          });
        }

        return acc;
      },
      [] as Array<CropsWithMissingHistoricalPracticeDataItem & { year: number }>
    );
  });

  const groupedByYear = cropsWithPractices.reduce((acc, item) => {
    if (!acc[item.year]) {
      acc[item.year] = [];
    }
    acc[item.year].push({
      fieldId: item.fieldId,
      crop: item.crop,
      previousMainCrop: item.previousMainCrop,
      hasMissingData: item.hasMissingData,
      isTillageDepthRequired: item.isTillageDepthRequired,
      assignedPractices: item.assignedPractices,
    });
    return acc;
  }, {} as CropsWithMissingHistoricalPracticeDataByYear);

  return groupedByYear;
};

// Unconditioned sibling of getCropsWithMissingHistoricalPracticeDataByYear for the all-fields data table view: every
// field and crop year is included regardless of enrollment/mandatory-year gating, and undersowing crops are included.
// Rows are not filtered by missing/editable status - the caller wants the full picture, and NA-ness for a given
// practice is decided by the UI layer via getInputRequirementConfig(..., ignoreCarbonProgramConditions: true).
export const getAllCropsPracticeDataByYear = (
  fields: FieldListField[],
  carbonProgram: CarbonProgram | null,
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined
): AllCropsPracticeDataByYear => {
  if (!allFieldsPractices || Object.keys(allFieldsPractices).length === 0) return {};

  const cropsWithPractices = fields.flatMap((field) => {
    const fieldPractices = allFieldsPractices[field.id];
    if (!fieldPractices || fieldPractices.crops.length === 0) return [];

    return fieldPractices.crops.reduce(
      (acc, crop) => {
        // prefer the backend-assigned year (same source as the year filter dropdown) over the date-derived
        // heuristic, so a crop lands in the year bucket the filter actually expects it in
        const cropYear =
          crop.year ??
          getCropYearFromDates(
            crop.cropType,
            moment(crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate),
            moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate)
          );
        if (!cropYear) return acc;

        const hasMissingGrowingPeriodDates = crop.seedingStartDate === null || crop.harvestEndDate === null;
        const hasGrowingPeriodDatesSoilPlus3DataCollection = !!crop.growingDatesSoilPlus3DataCollection;

        const tillages = fieldPractices.practices.filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES && practice.cropId === crop.id);
        const organicFertilizers = fieldPractices.practices.filter(
          (practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && practice.cropId === crop.id
        );
        const mineralFertilizers = fieldPractices.practices.filter(
          (practice) => practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS && practice.cropId === crop.id
        );
        const seedings = crop.seedingPracticeType ? [crop.seedingPracticeType] : [];

        const hasMissingTillages = tillages.length === 0;
        // decided with the field's real bundles, the same way Timeline does, so the table counts a missing depth too
        const inputRequirementConfig = getInputRequirementConfig(
          carbonProgram,
          fieldPractices.availablePracticeBundles ?? [],
          crop,
          isFieldEnrolled(fieldPractices.availablePracticeBundles ?? [], carbonProgram)
        );
        const isTillageDepthRequired = isRequired(inputRequirementConfig?.tillageDepth);
        const hasMissingTillageDepth =
          isTillageDepthRequired && tillages.some((practice) => practice.tillageDepth === null && practice.assignedPracticeId !== ZERO_UUID);

        const hasMissingTillageEndDate = tillages.some((practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID);

        const hasMissingOrganicFertilizers = organicFertilizers.length === 0;
        const hasMissingOrganicFertilizerEndDate = organicFertilizers.some(
          (practice) => practice.endDate === null && practice.assignedPracticeId !== ZERO_UUID
        );
        const hasMissingSeedings = seedings.length === 0;

        const hasSoilPlus3DataCollectionTillages = tillages.some((practice) => practice.soilPlus3DataCollection === true);
        const hasSoilPlus3TillageDepthTillages = tillages.some((practice) => practice.tillageDepthSoilPlus3DataCollection === true);
        const hasSoilPlus3ApplicationDatesTillages = tillages.some((practice) => practice.applicationDatesSoilPlus3DataCollection === true);
        const hasSoilPlus3DataCollectionOrganicFertilizers = organicFertilizers.some((practice) => practice.soilPlus3DataCollection === true);
        const hasSoilPlus3ApplicationDatesOrganicFertilizers = organicFertilizers.some((practice) => practice.applicationDatesSoilPlus3DataCollection === true);
        const hasSoilPlus3DataCollectionSeedings = crop.seedingSoilPlus3DataCollection === true;

        const hasAnyMissingData =
          hasMissingGrowingPeriodDates ||
          hasMissingTillages ||
          hasMissingTillageDepth ||
          hasMissingTillageEndDate ||
          hasMissingOrganicFertilizers ||
          hasMissingOrganicFertilizerEndDate ||
          hasMissingSeedings;

        const isEditable =
          hasGrowingPeriodDatesSoilPlus3DataCollection ||
          hasSoilPlus3DataCollectionTillages ||
          hasSoilPlus3TillageDepthTillages ||
          hasSoilPlus3ApplicationDatesTillages ||
          hasSoilPlus3DataCollectionOrganicFertilizers ||
          hasSoilPlus3ApplicationDatesOrganicFertilizers ||
          hasSoilPlus3DataCollectionSeedings;

        acc.push({
          year: cropYear,
          fieldId: field.id,
          crop,
          previousMainCrop: calculatePreviousCrop(fieldPractices.crops, crop),
          hasMissingData: hasAnyMissingData,
          isTillageDepthRequired,
          isEditable,
          assignedPractices: {
            [PRACTICE_SLUG.TILLAGES]: tillages,
            [PRACTICE_SLUG.ORGANIC_FERTILIZERS]: organicFertilizers,
            [PRACTICE_SLUG.MINERAL_FERTILIZERS]: mineralFertilizers,
            [PRACTICE_SLUG.SEEDINGS]: seedings,
          },
        });

        return acc;
      },
      [] as Array<AllCropsPracticeDataItem & { year: number }>
    );
  });

  const groupedByYear = cropsWithPractices.reduce((acc, item) => {
    if (!acc[item.year]) {
      acc[item.year] = [];
    }
    acc[item.year].push({
      fieldId: item.fieldId,
      crop: item.crop,
      previousMainCrop: item.previousMainCrop,
      hasMissingData: item.hasMissingData,
      isTillageDepthRequired: item.isTillageDepthRequired,
      isEditable: item.isEditable,
      assignedPractices: item.assignedPractices,
    });
    return acc;
  }, {} as AllCropsPracticeDataByYear);

  return groupedByYear;
};

export const getPracticeSlugsWithEntries = (
  fields: FieldListField[],
  carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse | undefined,
  carbonProgram: CarbonProgram | null,
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined,
  reportingYear: number | undefined,
  ignoreCarbonProgramConditions = false
): {
  practiceSlugsWithNonZeroIdEntries: PracticeSlug[];
  practiceSlugsWithAnyEntries: PracticeSlug[];
} => {
  const empty = { practiceSlugsWithNonZeroIdEntries: [], practiceSlugsWithAnyEntries: [] };
  if (!carbonProgramOutcomesResponse || !carbonProgram || !reportingYear) return { practiceSlugsWithNonZeroIdEntries: [], practiceSlugsWithAnyEntries: [] };

  const enrolledFields =
    ignoreCarbonProgramConditions || carbonProgram.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory
      ? fields
      : filterEnrolledFields(fields, allFieldsPractices, carbonProgram);
  if (enrolledFields.length === 0) return empty;
  if (!allFieldsPractices || Object.keys(allFieldsPractices).length === 0) return empty;

  const slugsNonZeroId = new Set<PracticeSlug>();
  const slugsAny = new Set<PracticeSlug>();

  for (const field of enrolledFields) {
    const fieldPractices = allFieldsPractices[field.id];
    if (fieldPractices && fieldPractices.crops.length > 0) {
      const availablePracticeBundles = fieldPractices.availablePracticeBundles ?? [];
      const mandatoryYearsByPracticeSlug = getMandatoryYearsByPracticeSlug(availablePracticeBundles, carbonProgram, reportingYear);
      const mandatoryCropYears = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.CROPS];
      const mandatoryTillageYears = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.TILLAGES];
      const mandatoryOrganicFertilizerYears = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.ORGANIC_FERTILIZERS];
      const mandatorySeedingYears = mandatoryYearsByPracticeSlug[PRACTICE_SLUG.SEEDINGS];

      for (const crop of fieldPractices.crops) {
        if ([CROP_TYPE_FCPO.MAIN, CROP_TYPE_FCPO.COVER].includes(crop.cropType)) {
          const cropYear = getCropYearFromDates(
            crop.cropType,
            moment(crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate),
            moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate)
          );
          if (cropYear && (ignoreCarbonProgramConditions || (mandatoryCropYears.includes(cropYear) && cropYear <= 2025))) {
            const tillages =
              ignoreCarbonProgramConditions || mandatoryTillageYears.length > 0
                ? fieldPractices.practices.filter((practice) => practice.type === PRACTICE_SLUG.TILLAGES && practice.cropId === crop.id)
                : null;
            const organicFertilizers =
              ignoreCarbonProgramConditions || mandatoryOrganicFertilizerYears.length > 0
                ? fieldPractices.practices.filter((practice) => practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS && practice.cropId === crop.id)
                : null;
            const seedings =
              ignoreCarbonProgramConditions || mandatorySeedingYears.length > 0 ? (crop.seedingPracticeType ? [crop.seedingPracticeType] : []) : null;

            const hasTillageNonZeroId = tillages !== null && tillages.some((p) => p.assignedPracticeId !== ZERO_UUID);
            const hasTillageAny = tillages !== null && tillages.length > 0;
            const hasOrganicFertilizerNonZeroId = organicFertilizers !== null && organicFertilizers.some((p) => p.assignedPracticeId !== ZERO_UUID);
            const hasOrganicFertilizerAny = organicFertilizers !== null && organicFertilizers.length > 0;
            const hasSeedingNonZeroId = seedings !== null && seedings.some((id) => id !== ZERO_UUID);
            const hasSeedingAny = seedings !== null && seedings.length > 0;

            if (hasTillageNonZeroId) slugsNonZeroId.add(PRACTICE_SLUG.TILLAGES);
            if (hasTillageAny) slugsAny.add(PRACTICE_SLUG.TILLAGES);
            if (hasOrganicFertilizerNonZeroId) slugsNonZeroId.add(PRACTICE_SLUG.ORGANIC_FERTILIZERS);
            if (hasOrganicFertilizerAny) slugsAny.add(PRACTICE_SLUG.ORGANIC_FERTILIZERS);
            if (hasSeedingNonZeroId) slugsNonZeroId.add(PRACTICE_SLUG.SEEDINGS);
            if (hasSeedingAny) slugsAny.add(PRACTICE_SLUG.SEEDINGS);
          }
        }
      }
    }
  }

  return {
    practiceSlugsWithNonZeroIdEntries: Array.from(slugsNonZeroId),
    practiceSlugsWithAnyEntries: Array.from(slugsAny),
  };
};

export const getMissingHistoricYieldDataOpenTasksCount = (cropsWithMissingHistoricYieldData: CropsWithMissingHistoricYieldDataByCropId): number =>
  Object.values(cropsWithMissingHistoricYieldData).reduce(
    (total, cropData) => total + Object.values(cropData.years).filter((data) => !data.isCompleted).length,
    0
  );

export const getCropsWithMissingHistoricalPracticeCount = (
  cropsWithMissingHistoricalPracticeDataByYear: CropsWithMissingHistoricalPracticeDataByYear
): number => {
  if (!cropsWithMissingHistoricalPracticeDataByYear) return 0;

  return Object.values(cropsWithMissingHistoricalPracticeDataByYear).reduce(
    (total, yearData) => total + yearData.filter((item) => item.hasMissingData).length,
    0
  );
};

export const hasItemsWithMissingData = (itemsWithMissingData: ItemsWithMissingData | undefined): boolean => {
  if (!itemsWithMissingData) return false;

  return (
    itemsWithMissingData.cropsWithMissingGrowingDates.length > 0 ||
    itemsWithMissingData.cropsWithMissingPractices.length > 0 ||
    itemsWithMissingData.practicesWithMissingData.length > 0 ||
    itemsWithMissingData.missingCrops.length > 0 ||
    itemsWithMissingData.cropsWithDataToConfirm.length > 0 ||
    itemsWithMissingData.cropsWithVerificationEditingRequests.length > 0 ||
    itemsWithMissingData.coverCropsWithMissingRedzoneException.length > 0 ||
    itemsWithMissingData.cropsWithMissingSecondaryDataStatus.length > 0
  );
};

export const getItemsWithMissingDataSortedByStartTime = (itemsWithMissingData: ItemsWithMissingData | undefined): ItemWithMissingData[] => {
  if (!itemsWithMissingData) return [];

  const uniqueItemsWithMissingData = [
    ...itemsWithMissingData.cropsWithMissingGrowingDates,
    ...itemsWithMissingData.missingCrops,
    ...itemsWithMissingData.cropsWithMissingPractices,
    ...itemsWithMissingData.practicesWithMissingData,
    ...itemsWithMissingData.cropsWithDataToConfirm,
    ...itemsWithMissingData.cropsWithVerificationEditingRequests,
    ...itemsWithMissingData.coverCropsWithMissingRedzoneException,
    ...itemsWithMissingData.cropsWithMissingSecondaryDataStatus,
  ];

  return uniqueItemsWithMissingData.sort((a, b) => a.startTime.diff(b.startTime));
};

export const createCropItems = (
  fieldCropOverviewResponse: FieldPracticeResponse,
  fieldId: string,
  baselineYears: number[],
  isFieldEnrolled: boolean,
  t: TFunction
): TimelineCalendarItemsProps<CropItemData>[] =>
  fieldCropOverviewResponse.crops.map((crop) => {
    const practices = fieldCropOverviewResponse.practices.filter((practice) => practice.cropId === crop.id);
    const activePracticeBundles = fieldCropOverviewResponse.availablePracticeBundles.map((bundle) => {
      if (!bundle.active || bundle.slug === "crops") return { ...bundle, hasMissingData: false };
      if (bundle.slug === PRACTICE_SLUG.ORGANIC_FERTILIZERS) {
        const hasOrganicFertilizerEntry = practices.some((practice) => practice.cropId === crop.id && practice.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS);
        const hasMineralFertilizerEntry = practices.some((practice) => practice.cropId === crop.id && practice.type === PRACTICE_SLUG.MINERAL_FERTILIZERS);
        return { ...bundle, hasMissingData: !hasOrganicFertilizerEntry || !hasMineralFertilizerEntry };
      }
      if (bundle.slug === PRACTICE_SLUG.TILLAGES) {
        const hasTillageEntry = practices.some((practice) => practice.cropId === crop.id && practice.type === PRACTICE_SLUG.TILLAGES);
        const hasSeedingTypeEntry = !!crop.seedingPracticeType;
        return { ...bundle, hasMissingData: !hasTillageEntry || !hasSeedingTypeEntry };
      }
      return { ...bundle, hasMissingData: false };
    });

    const className = crop.cropType === CROP_TYPE_FCPO.UNDERSOWING ? "real-undersowing-crop" : crop.cropType === CROP_TYPE_FCPO.COVER ? "real-cover-crop" : "";
    const startTime = moment(crop.seedingStartDate ?? crop.fallbackDates.seedingStartDate);
    const endTime = moment(crop.harvestEndDate ?? crop.fallbackDates.harvestEndDate);
    const isBaseline = isFieldEnrolled && baselineYears.includes(endTime.year());
    const isMandatory = isFieldEnrolled && crop.cropType === CROP_TYPE_FCPO.MAIN && (isBaseline || endTime.year() === moment().year());

    return {
      id: crop.id,
      group: fieldId,
      title: "",
      start_time: startTime,
      end_time: endTime,
      practiceType: getCropPracticeType(crop.cropType),
      canResize: "both" as const,
      className,
      data: {
        cropId: crop.cropId,
        cropMixId: crop.cropMixId,
        cropName: t(`${PRACTICE_SLUG.CROPS}.${crop.translationSlug}`, { ns: "practices" }),
        cropPractices: {
          activePracticeBundles: activePracticeBundles,
          practices: practices,
          crops: fieldCropOverviewResponse.crops,
          fieldHasFailedVerification: fieldCropOverviewResponse.fieldHasFailedVerification,
        },
        cropType: crop.cropType,
        fertilizerManagements: fieldCropOverviewResponse.fertilizerManagements,
        fieldId,
        growingDatesSoilPlus3DataCollection: crop.growingDatesSoilPlus3DataCollection ?? false,
        hasMissingGrowingDates: crop.seedingStartDate === null || crop.harvestEndDate === null,
        hasResidue: crop.hasResidue,
        isBaseline: baselineYears.includes(crop.year!),
        isConfirmed: crop.isConfirmed,
        isMandatory: isMandatory,
        isMandatoryCoverCrop: crop.mandatory,
        isSecondaryData: crop.isSecondaryData,
        redzoneExceptionId: crop.redzoneExceptionId,
        seedingPracticeId: crop.seedingPracticeId,
        seedingSoilPlus3DataCollection: crop.seedingSoilPlus3DataCollection ?? false,
        year: crop.year,
        yieldPerHa: crop.yieldPerHa,
        yieldPerHaUnit: crop.yieldPerHaUnit,
        yieldSoilPlus3DataCollection: crop.yieldSoilPlus3DataCollection ?? false,
      },
    };
  });

export const isCheckboxClick = (target: HTMLElement | null): boolean =>
  !!(
    target?.closest('[data-testid*="checkbox"]') ||
    target?.closest(".ant-checkbox") ||
    target?.classList.contains("ant-checkbox") ||
    target?.classList.contains("ant-checkbox-input")
  );

export const getFieldOpenTasksCount = (itemsWithMissingData: ItemsWithMissingData, fieldVerificationEditingRequests: VerificationEditingRequest[]): number => {
  if (!itemsWithMissingData) return 0;

  return (
    (itemsWithMissingData.cropsWithMissingGrowingDates?.length ?? 0) +
    (itemsWithMissingData.cropsWithMissingPractices?.length ?? 0) +
    (itemsWithMissingData.practicesWithMissingData?.length ?? 0) +
    (itemsWithMissingData.missingCrops?.length ?? 0) +
    (itemsWithMissingData.cropsWithDataToConfirm?.length ?? 0) +
    (itemsWithMissingData.coverCropsWithMissingRedzoneException?.length ?? 0) +
    (itemsWithMissingData.cropsWithMissingSecondaryDataStatus?.length ?? 0) +
    (fieldVerificationEditingRequests?.filter((request) => !request.completedAt)?.length ?? 0)
  );
};

// A stable string that changes whenever a request is added, removed or completed, for use as a memo dependency
export const getVerificationEditingRequestsSnapshot = (verificationEditingRequests: VerificationEditingRequest[]): string =>
  verificationEditingRequests
    .map((request) => `${request.id}:${request.fieldId}:${request.completedAt}`)
    .sort()
    .join(",");

export const getFarmOpenTasksCount = (
  itemsWithMissingDataByFieldId: ItemsWithMissingDataByFieldId,
  farmVerificationEditingRequests: VerificationEditingRequest[]
): { fieldsWithOpenTasksCount: number; openTasksCount: number } => {
  if (!itemsWithMissingDataByFieldId) return { fieldsWithOpenTasksCount: 0, openTasksCount: 0 };

  const numberOfFieldsWithOpenTasks = Object.entries(itemsWithMissingDataByFieldId).reduce(
    (acc, [fieldId, itemsWithMissingData]) => {
      const fieldVerificationEditingRequests = farmVerificationEditingRequests.filter((request) => request.fieldId === fieldId);
      const fieldOpenTasksCount = getFieldOpenTasksCount(itemsWithMissingData, fieldVerificationEditingRequests);
      if (fieldOpenTasksCount > 0)
        return {
          fieldsWithOpenTasksCount: acc.fieldsWithOpenTasksCount + 1,
          openTasksCount: acc.openTasksCount + fieldOpenTasksCount,
        };
      return acc;
    },
    { fieldsWithOpenTasksCount: 0, openTasksCount: 0 }
  );

  return numberOfFieldsWithOpenTasks;
};

export const getCropOpenTasksCount = (cropId: string, itemsWithMissingData: ItemsWithMissingData): number => {
  if (!cropId) return 0;

  const hasVerificationEditingRequests = itemsWithMissingData.cropsWithVerificationEditingRequests?.some((crop) => crop.id === cropId);
  const hasMissingPractices = itemsWithMissingData.cropsWithMissingPractices?.some((crop) => crop.id === cropId);
  const hasPracticesWithMissingData = itemsWithMissingData.practicesWithMissingData?.some((crop) => crop.id === cropId);
  const hasCropsWithMissingGrowingDates = itemsWithMissingData.cropsWithMissingGrowingDates?.some((crop) => crop.id === cropId);
  const hasDataToConfirm = itemsWithMissingData.cropsWithDataToConfirm?.some((crop) => crop.id === cropId);
  const isPlaceholderWithMissingMandatoryCrop = itemsWithMissingData.missingCrops?.some((crop) => crop.id === cropId);
  const hasCoverCropsWithMissingRedzoneException = itemsWithMissingData.coverCropsWithMissingRedzoneException?.some((crop) => crop.id === cropId);
  const hasCropsWithMissingSecondaryDataStatus = itemsWithMissingData.cropsWithMissingSecondaryDataStatus?.some((crop) => crop.id === cropId);

  return [
    hasVerificationEditingRequests,
    hasMissingPractices,
    hasPracticesWithMissingData,
    hasCropsWithMissingGrowingDates,
    hasDataToConfirm,
    isPlaceholderWithMissingMandatoryCrop,
    hasCoverCropsWithMissingRedzoneException,
    hasCropsWithMissingSecondaryDataStatus,
  ].reduce((acc, curr) => acc + (curr ? 1 : 0), 0);
};

const FIELD_LEVEL_EDITING_REQUEST_TYPES: EditingRequestType[] = [
  EDITING_REQUEST_TYPE.FIELD_SHAPE,
  EDITING_REQUEST_TYPE.FIELD_SPLIT,
  EDITING_REQUEST_TYPE.FIELD_REDZONE,
];

export const hasFieldLevelVerificationEditingRequests = (
  fieldId: string,
  verificationEditingRequests: GetVerificationEditingRequestsResponse | undefined
): boolean => {
  if (!fieldId || !verificationEditingRequests || verificationEditingRequests.length === 0) return false;

  return verificationEditingRequests.some(
    (request) => request.fieldId === fieldId && FIELD_LEVEL_EDITING_REQUEST_TYPES.includes(request.requestType) && !request.completedAt
  );
};

export const getEnrolledFields = (
  fields: FieldListField[],
  carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse,
  carbonProgram: CarbonProgram | null,
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined
): FieldListField[] => {
  if (!carbonProgramOutcomesResponse || !fields || fields.length === 0 || !carbonProgram) return [];
  if (isAutoEnrolledInventoryProgram(carbonProgram)) return fields;

  return fields.filter((field) => {
    if (!allFieldsPractices) return false;
    const practiceBundle = allFieldsPractices[field.id]?.availablePracticeBundles ?? [];
    return isFieldEnrolled(practiceBundle, carbonProgram ?? null);
  });
};

export const getEnrolledFieldsCount = (
  fields: FieldListField[],
  carbonProgramOutcomesResponse: CarbonProgramOutcomesResponse,
  carbonProgram: CarbonProgram | null,
  allFieldsPractices: Record<string, FieldPracticeResponse> | undefined
): number => getEnrolledFields(fields, carbonProgramOutcomesResponse, carbonProgram, allFieldsPractices).length;

export const formatPayoutsPostSubmission = ({
  carbonProgramOutcomes,
  carbonProgram,
  targetYear,
  inventoryPayoutMin,
  inventoryPayoutMax,
  isSoilPlus3DataGapFeatureActive,
  isSoilPlus3RewardsFeatureActive = false,
}: {
  carbonProgramOutcomes: CarbonProgramOutcomesResponse | null;
  carbonProgram: CarbonProgram | null;
  targetYear: number;
  inventoryPayoutMin: number;
  inventoryPayoutMax: number;
  isSoilPlus3DataGapFeatureActive: boolean;
  isSoilPlus3RewardsFeatureActive?: boolean;
}) => {
  if (!carbonProgramOutcomes || !carbonProgram || !targetYear) return null;

  const isInventoryProgram = carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;
  const impact = computeCarbonProgramImpact(carbonProgramOutcomes, targetYear);

  // TODO: Remove once rollover to Soil+ 3.0 is complete
  const interventionPayoutMin = isSoilPlus3DataGapFeatureActive ? impact.totalPayoutMin : impact.totalPayoutAvg;
  const interventionPayoutMax = isSoilPlus3DataGapFeatureActive ? impact.totalPayoutMax : impact.totalPayoutAvg;
  const pointPrice = carbonProgram.fieldsManagement.payouts?.removals?.pointPrice ?? 0;
  const { eligiblePayout } = calculateAllotmentPayouts(impact.allotmentAmount, impact.bonusAllotmentAmount, pointPrice);
  const displayedInterventionPayoutMin = isSoilPlus3RewardsFeatureActive ? capPayoutAtEligible(interventionPayoutMin, eligiblePayout) : interventionPayoutMin;
  const interventionCO2Min = isSoilPlus3DataGapFeatureActive ? impact.totalCo2Min : impact.totalCo2Avg;
  const interventionCO2Max = isSoilPlus3DataGapFeatureActive ? impact.totalCo2Max : impact.totalCo2Avg;

  return {
    totalPayoutMin: isInventoryProgram ? inventoryPayoutMin : displayedInterventionPayoutMin,
    totalPayoutMax: isInventoryProgram ? inventoryPayoutMax : interventionPayoutMax,
    totalCO2Min: isInventoryProgram ? impact.totalCo2Min : interventionCO2Min,
    totalCO2Max: isInventoryProgram ? impact.totalCo2Max : interventionCO2Max,
  };
};

type SubmissionBarTaskCounts = {
  isSoilPlus3DataGapFeatureActive: boolean;
  isRetentionFarm: boolean;
  isInventoryProgram: boolean;
  missingHistoricYieldDataOpenTasksCount: number;
  openMissingDataTasksCount: number;
  openMissingDocumentsCount: number;
  openMissingHistoricalPracticeDataTasksCount: number;
  openFarmSourcesTasksCount: number;
  openCoverCropDetectionsCount: number;
  openRedzoneTasksCount: number;
};

export const getSubmissionBarTasksTriggerButtonCount = ({
  isSoilPlus3DataGapFeatureActive,
  isRetentionFarm,
  isInventoryProgram,
  missingHistoricYieldDataOpenTasksCount,
  openMissingDataTasksCount,
  openMissingDocumentsCount,
  openMissingHistoricalPracticeDataTasksCount,
  openFarmSourcesTasksCount,
  openCoverCropDetectionsCount,
  openRedzoneTasksCount,
}: SubmissionBarTaskCounts): number => {
  const hasMissingData = openMissingDataTasksCount > 0;
  const hasFertilizerSources = openFarmSourcesTasksCount > 0;
  const hasCoverCropDetections = openCoverCropDetectionsCount > 0;
  const hasOutstandingRedzones = openRedzoneTasksCount > 0;

  const isHistoricalDataGapActive = isSoilPlus3DataGapFeatureActive && isRetentionFarm;
  const hasMissingHistoricYieldData = isHistoricalDataGapActive && missingHistoricYieldDataOpenTasksCount > 0;
  const hasMissingDocuments = openMissingDocumentsCount > 0;
  const hasMissingHistoricalPracticeData = isHistoricalDataGapActive && !isInventoryProgram && openMissingHistoricalPracticeDataTasksCount > 0;

  return [
    hasMissingHistoricYieldData,
    hasMissingData,
    hasMissingDocuments,
    hasMissingHistoricalPracticeData,
    hasFertilizerSources,
    hasCoverCropDetections,
    hasOutstandingRedzones,
  ].reduce((acc, curr) => acc + (curr ? 1 : 0), 0);
};

// Document tasks only count towards submission for programs that go through verification
export const getMissingDocumentsCounts = (verificationFarmTasks: VerificationsFarmTasks[] | undefined, carbonProgram: CarbonProgram | null | undefined) => {
  const requiredDocumentTasks = carbonProgram?.verificationRequired ? verificationFarmTasks ?? [] : [];
  return {
    totalMissingDocumentsCount: requiredDocumentTasks.length,
    openMissingDocumentsCount: requiredDocumentTasks.filter((task) => !task.completedAt).length,
  };
};

export const YieldFormValidationError = ({
  value,
  valueUnit,
  isZeroYieldsAllowed,
  isRequired,
  t,
}: {
  value: number | null;
  valueUnit: HarvestUnitValue | null;
  isZeroYieldsAllowed: boolean;
  isRequired: boolean | undefined;
  t: TFunction;
}) => {
  if (!isRequired) return Promise.resolve();
  if (isZeroYieldsAllowed && (value === null || value === undefined)) return Promise.reject(new Error(t("cropForm.errorMessages.yieldEmptyWarning")));
  if (value === null || value === undefined) return Promise.reject(new Error(t("cropForm.errorMessages.yieldRequired")));
  if (valueUnit === null || valueUnit === undefined) return Promise.reject(new Error(t("cropForm.errorMessages.yieldUnitRequired")));
  return Promise.resolve();
};

export const YieldFormValidationWarning = ({
  value,
  isZeroYieldsAllowed,
  isRequired,
  initialValue,
  t,
}: {
  value: number | null;
  isZeroYieldsAllowed: boolean;
  isRequired: boolean | undefined;
  initialValue?: number | null;
  t: TFunction;
}) => {
  if (!isRequired) return Promise.resolve();
  if (value === 0 && initialValue === 0) return Promise.resolve();
  if (isZeroYieldsAllowed && value === 0) return Promise.reject(new Error(t("cropForm.errorMessages.yieldZeroWarning")));
  return Promise.resolve();
};

export const sortFieldsBySortOption = (fields: FieldListField[], sortOption: FIELD_SORT_OPTION | null, enrolledFieldIds: Set<string>): FieldListField[] => {
  if (!sortOption) return fields;

  const sortedFields = [...fields];
  const compareNames = (fieldA: FieldListField, fieldB: FieldListField) =>
    fieldA.name.localeCompare(fieldB.name, undefined, { numeric: true, sensitivity: "base" });
  const isEnrolledAsNumber = (field: FieldListField) => Number(enrolledFieldIds.has(field.id));

  switch (sortOption) {
    case FIELD_SORT_OPTION.NAME_A_TO_Z:
      return sortedFields.sort(compareNames);
    case FIELD_SORT_OPTION.NAME_Z_TO_A:
      return sortedFields.sort((fieldA, fieldB) => compareNames(fieldB, fieldA));
    case FIELD_SORT_OPTION.SIZE_LARGEST_FIRST:
      return sortedFields.sort((fieldA, fieldB) => (fieldB.size ?? 0) - (fieldA.size ?? 0));
    case FIELD_SORT_OPTION.SIZE_SMALLEST_FIRST:
      return sortedFields.sort((fieldA, fieldB) => (fieldA.size ?? 0) - (fieldB.size ?? 0));
    case FIELD_SORT_OPTION.NOT_ENROLLED_FIRST:
      return sortedFields.sort((fieldA, fieldB) => isEnrolledAsNumber(fieldA) - isEnrolledAsNumber(fieldB));
    default:
      return sortedFields;
  }
};

export const isSecondaryDataQuestionShown = (crops: Pick<CropFCPO, "cropType" | "isConfirmed">[], accountingMethodology?: AccountingMethodology): boolean =>
  crops.length > 0 &&
  !getEnvironment().production &&
  accountingMethodology === ACCOUNTING_METHODOLOGY.intervention &&
  crops.every((crop) => !crop.isConfirmed && crop.cropType === CROP_TYPE_FCPO.MAIN);

export const getSharedSecondaryDataAnswer = (crops: Pick<CropFCPO, "isSecondaryData">[]): boolean | null => {
  const firstAnswer = crops[0]?.isSecondaryData;
  if (typeof firstAnswer !== "boolean") return null;
  return crops.every((crop) => crop.isSecondaryData === firstAnswer) ? firstAnswer : null;
};
